import { ImageElement } from './ImageElement';

/**
 * LogoElement extends ImageElement to represent logo images that are coupled
 * to a specific video track (typically the main video track).
 *
 * When a logo is coupled, it should be automatically adjusted to match the
 * bounds of its coupled track whenever that track's timing changes.
 */
export class LogoElement extends ImageElement {
  /**
   * ID of the track this logo is coupled to (e.g., transcript track ID).
   * When set, this logo should move/resize with the coupled track.
   */
  private _coupledTrackId: string | null = null;

  get coupledTrackId(): string | null {
    return this._coupledTrackId;
  }

  set coupledTrackId(trackId: string | null) {
    this._coupledTrackId = trackId;
  }

  /**
   * Returns true if this logo is coupled to a track.
   */
  isCoupledToTrack(): boolean {
    return this._coupledTrackId !== null;
  }

  /**
   * Returns true if this logo is coupled to the specified track.
   */
  isCoupledTo(trackId: string): boolean {
    return this._coupledTrackId === trackId;
  }

  /**
   * Creates a LogoElement from an existing ImageElement.
   * Preserves all properties and converts the instance.
   */
  static fromImageElement(imageElement: ImageElement): LogoElement {
    const logoElement = new LogoElement(imageElement.toJSON());
    return logoElement;
  }

  /**
   * Captures the logo element's state for undo/redo.
   * Includes the coupling information.
   */
  override getState(): Record<string, any> {
    return {
      ...super.getState(),
      coupledTrackId: this._coupledTrackId,
    };
  }

  /**
   * Restores the logo element's state from a state record.
   * Restores the coupling information.
   */
  override restoreState(state: Record<string, any>): void {
    super.restoreState(state);
    if (state.coupledTrackId !== undefined) {
      this._coupledTrackId = state.coupledTrackId;
    }
  }

  /**
   * Creates a deep clone of this logo element.
   * Preserves the coupling information.
   */
  override clone(): LogoElement {
    const el = new LogoElement({
      ...this.getState(),
      source: this.source,
      fit: this.fit,
    });
    // Manually set the private coupling property after construction
    el._coupledTrackId = this._coupledTrackId;
    return el;
  }

  /**
   * Override update to clear coupling when logo is manually modified.
   * Any manual update to time/duration breaks the coupling relationship.
   */
  override update(props: Partial<LogoElement>): void {
    // If time or duration is being updated, clear the coupling
    if ('time' in props || 'duration' in props) {
      this._coupledTrackId = null;
    }
    super.update(props);
  }

  /**
   * Internal method to update logo position without breaking coupling.
   * Used by automatic coupling adjustment logic.
   */
  updateCoupledPosition(time: number, duration: number): void {
    // Directly call parent's update to avoid clearing coupling
    super.update({ time, duration });
  }

  isLogoElement() {
    return true;
  }
}
